import { useId, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckHeader } from '@/features/check/CheckHeader';
import { formatMoney, formatPercent, parseMoneyInput, toInputText } from '@/lib/money';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import type { Check, Location, TipOption } from '@/api/types';

interface TipScreenProps {
    location: Location;
    check: Check;
    /**
     * A tip the guest already settled on, in minor units, restored when they come back to this step.
     *
     * Undefined on a first visit, which is not the same as 0 - zero is a guest who deliberately declined.
     */
    initialTipCents?: number;
    onPay?: (tipCents: number) => void;
    onBack?: () => void;
}

/**
 * One choice in the tip selector: an amount the server priced, or the guest's own.
 *
 * Nothing is synthesised. The three percentages the location configured are what appear, and a guest who
 * does not want to tip enters zero in the custom field — matching the site this replaces, which offered no
 * decline button either.
 */
type Choice = { kind: 'option'; option: TipOption } | { kind: 'custom' };

function choiceKey(choice: Choice): string {
    if (choice.kind === 'custom') return 'custom';

    return 'option-' + String(choice.option.percentage) + '-' + String(choice.option.amountCents);
}

/**
 * Where the guest decides what to add.
 *
 * Presentational like the check screen: it takes the location and check and fetches nothing, so it renders
 * from fixtures and is testable without a network.
 *
 * The rule this screen lives by is that it NEVER computes a percentage of the check. Every offered amount is
 * priced by the server and rendered as given; `percentage` exists only to write "20%" on the button. Two
 * implementations of the same percentage disagree eventually, and the legacy app is the proof — it floors the
 * amount it charges and rounds the string it displays, so a $102.53 check at 20% charges $20.50 while showing
 * "$20.51". The only arithmetic here is one addition, and whatever the guest typed themselves.
 */
export function TipScreen({ location, check, initialTipCents, onPay, onBack }: TipScreenProps) {
    const { t } = useTranslation();
    const { currency, locale } = location;
    const money = (minorUnits: number) => formatMoney(minorUnits, currency, locale);

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    const customInputId = useId();
    const customErrorId = useId();

    /** The configured percentages, then the guest's own — always offered, never configurable away. */
    const presets = useMemo(
        () => check.tipOptions.map((option): Choice => ({ kind: 'option', option })),
        [check.tipOptions],
    );

    const custom: Choice = { kind: 'custom' };

    /**
     * What this check opens on.
     *
     * A tip the guest already chose wins - they reached the next step and came back, and finding their choice
     * quietly reset to the house default is the screen deciding for them about money. Failing that, the
     * option the location marked default, else the first it sent.
     *
     * Falls back to the custom field only when the server sent no options at all, which is a
     * misconfiguration rather than a setting — better to let the guest type an amount than to strand them
     * on a screen with nothing to choose.
     */
    const defaultChoice = useMemo<Choice>(() => {
        const chosen =
            initialTipCents === undefined
                ? undefined
                : check.tipOptions.find((option) => option.amountCents === initialTipCents);

        if (chosen) return { kind: 'option', option: chosen };

        // A returning amount that matches no option was typed, so put them back in the custom field.
        if (initialTipCents) return { kind: 'custom' };

        const preferred = check.tipOptions.find((option) => option.isDefault) ?? check.tipOptions[0];

        return preferred ? { kind: 'option', option: preferred } : { kind: 'custom' };
    }, [check.tipOptions, initialTipCents]);

    const [selected, setSelected] = useState<Choice>(defaultChoice);

    /*
     * A returning custom amount is written back as digits the guest could have typed, using this location's
     * decimal separator, so `parseMoneyInput` reads it back as the same integer it came from.
     */
    const [customText, setCustomText] = useState(() =>
        defaultChoice.kind === 'custom' && initialTipCents ? toInputText(initialTipCents, currency, locale) : '',
    );

    /*
     * A different check starts over.
     *
     * React reuses this component when the check changes, so without this a tip chosen for one check - and
     * any amount typed for it - follows the guest onto the next one. Resetting during render rather than in
     * an effect means the stale figure is never shown, not even for a frame. This is React's documented way
     * to adjust state when a prop changes; a caller passing `key={check.id}` would do the same, but that puts
     * the correctness of a charge in the caller's hands.
     */
    const [renderedCheckId, setRenderedCheckId] = useState(check.id);

    if (renderedCheckId !== check.id) {
        setRenderedCheckId(check.id);
        setSelected(defaultChoice);
        setCustomText('');
    }

    /*
     * A selection only counts while the check still offers it.
     *
     * This component survives a change of check - React reuses the instance, so the previous check's choice
     * is still in state. Without this, an amount priced against one check follows the guest onto a check that
     * offers different options entirely and quietly adds itself to the total. Clamping during render means an
     * unoffered selection can never reach `tipCents`.
     */
    const active = useMemo<Choice>(() => {
        if (selected.kind === 'custom') return selected;

        const stillOffered = presets.some((choice) => choiceKey(choice) === choiceKey(selected));

        return stillOffered ? selected : defaultChoice;
    }, [selected, presets, defaultChoice]);

    const customCents = customText.trim() ? parseMoneyInput(customText, currency, locale) : null;

    /*
     * Only raised once there is something to be wrong about. Telling a guest their empty field is invalid the
     * moment they pick "Custom" is scolding them for not having typed yet.
     */
    const customError = (() => {
        if (active.kind !== 'custom' || !customText.trim()) return null;
        if (customCents === null) return t('tip.customInvalid', { example: money(500) });

        return null;
    })();

    /** Null means "not payable yet" — a custom field that is empty, unreadable or below the floor. */
    const tipCents = (() => {
        if (active.kind === 'option') return active.option.amountCents;

        return customError || customCents === null ? null : customCents;
    })();

    const canPay = tipCents !== null && !check.isClosed;

    // The amount due, not the check total: on a check somebody has part-paid they differ, and the tip is added
    // to what is actually owed. The two are the same number on every other check.
    const totalCents = check.amountDueCents + (tipCents ?? 0);

    const label = (choice: Choice): { title: string; detail: string | null } => {
        if (choice.kind === 'custom') return { title: t('tip.custom'), detail: null };

        const { percentage, amountCents } = choice.option;

        return { title: formatPercent(percentage, locale), detail: money(amountCents) };
    };

    const renderChoice = (choice: Choice) => {
        const { title, detail } = label(choice);
        const isSelected = choiceKey(choice) === choiceKey(active);

        return (
            <label key={choiceKey(choice)} className="block cursor-pointer">
                <input
                    type="radio"
                    name="tip"
                    className="peer sr-only"
                    checked={isSelected}
                    onChange={() => setSelected(choice)}
                />
                {/*
                  * The focus ring goes on what is visible. The input is screen-reader-only, so its own
                  * outline would be painted on nothing and a keyboard guest would see no focus at all.
                  */}
                <span className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border border-border bg-tip px-1.5 py-2.5 text-tip-foreground transition-colors peer-checked:border-transparent peer-checked:bg-tip-selected peer-checked:text-tip-selected-foreground peer-focus-visible:outline-solid peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-foreground">
                    <span className="text-lg leading-none font-semibold">{title}</span>
                    {detail && (
                        <span className="tabular text-sm leading-none opacity-80">{detail}</span>
                    )}
                </span>
            </label>
        );
    };

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <CheckHeader location={location} check={check} />

                {/* Bottom padding clears the fixed footer, as on the check screen. */}
                <main className="px-5 pb-44">
                    <div className="overflow-hidden rounded-xl border border-border bg-card px-4 py-4 shadow-xs">
                        <h2 className="text-lg font-semibold tracking-tight">{t('tip.title')}</h2>

                        {check.surchargesTotalCents > 0 && (
                            /*
                             * A service charge the POS already applied, and it is inside what the guest is
                             * about to pay. A large-party gratuity arrives this way, so asking for a tip on
                             * top without saying so is how somebody tips twice by accident.
                             */
                            <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                                {t('tip.serviceCharge', { amount: money(check.surchargesTotalCents) })}
                            </p>
                        )}

                        <fieldset className="mt-4 flex flex-col gap-2">
                            {/*
                              * Native radios, visually hidden behind the styled labels. They bring arrow-key
                              * navigation, the "2 of 4" announcement and grouped semantics for free — all of
                              * which a div with role="radio" has to reimplement, usually badly.
                              *
                              * Two rows, one group: the three configured percentages, then the guest's own
                              * amount across the full width. Four in a three-column grid would leave an
                              * orphaned cell.
                              */}
                            <legend className="sr-only">{t('tip.optionsLabel')}</legend>

                            {presets.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">{presets.map(renderChoice)}</div>
                            )}

                            {renderChoice(custom)}
                        </fieldset>

                        {active.kind === 'custom' && (
                            <div className="mt-4">
                                <label htmlFor={customInputId} className="text-sm font-medium">
                                    {t('tip.customLabel')}
                                </label>

                                <input
                                    id={customInputId}
                                    /*
                                     * `inputMode="decimal"` rather than `type="number"`: a number input
                                     * silently discards what it cannot parse, so a guest whose keypad offers a
                                     * comma watches their typing vanish. Text plus a decimal keypad keeps every
                                     * character and lets `parseMoneyInput` decide what it means.
                                     */
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    value={customText}
                                    onChange={(event) => setCustomText(event.target.value)}
                                    placeholder={t('tip.customPlaceholder')}
                                    aria-invalid={customError ? true : undefined}
                                    aria-describedby={customError ? customErrorId : undefined}
                                    className="tabular mt-1.5 h-12 w-full rounded-xl border border-border bg-background px-3 text-base text-foreground aria-invalid:border-destructive"
                                />

                                {customError && (
                                    // `role="alert"` so the reason is spoken, not only drawn.
                                    <p id={customErrorId} role="alert" className="mt-1.5 text-sm text-destructive">
                                        {customError}
                                    </p>
                                )}
                            </div>
                        )}

                        <dl className="tabular mt-4 flex flex-col gap-2 border-t border-border pt-3.5 text-sm">
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">
                                    {check.amountPaidCents > 0 ? t('check.amountDue') : t('tip.checkLine')}
                                </dt>
                                <dd>{money(check.amountDueCents)}</dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">{t('tip.tipLine')}</dt>
                                <dd>{money(tipCents ?? 0)}</dd>
                            </div>
                        </dl>
                    </div>

                    {onBack && (
                        <div className="mt-3 text-center">
                            <button
                                type="button"
                                onClick={onBack}
                                // min-h-11 keeps the tap target at 44px; the text alone came to 36.
                                className="inline-flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4"
                            >
                                {t('tip.back')}
                            </button>
                        </div>
                    )}
                </main>
            </div>

            <div className="pointer-events-none fixed inset-x-0 bottom-0">
                <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                    <div className="h-8 bg-gradient-to-t from-background/90 to-transparent" />

                    <footer className="pointer-events-auto border-t border-border bg-background/90 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-md">
                        <div className="tabular mb-3.5 flex items-baseline justify-between gap-3">
                            <span className="text-[0.9375rem] font-medium text-muted-foreground">
                                {t('check.total')}
                            </span>
                            {/*
                              * The total moves as the guest taps between options, and a guest reading by screen
                              * reader gets no repaint to notice. `aria-live="polite"` speaks the new figure once
                              * they settle, which is the one number they came here to decide.
                              */}
                            <span
                                aria-live="polite"
                                className="text-[1.75rem] leading-none font-semibold tracking-tight"
                            >
                                {money(totalCents)}
                            </span>
                        </div>

                        <Button
                            size="lg"
                            className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                            disabled={!canPay}
                            onClick={() => tipCents !== null && onPay?.(tipCents)}
                        >
                            {check.isClosed
                                ? t('check.closedAction')
                                : t('tip.pay', { amount: money(totalCents) })}
                        </Button>
                    </footer>
                </div>
            </div>
        </div>
    );
}
